/**
 * Web Audio crossfade mixer for the music bed. Uses dual HTMLAudioElements
 * connected through GainNodes only when crossfade is requested; standard mode
 * plays through the native HTMLAudioElement path (no MediaElementSource).
 *
 * Because playback alternates between the two elements across crossfades, the
 * playback hook attaches its listener set to BOTH elements (getElements()) and
 * routes each event by the element's current role (elementRole()) — see
 * media-event-router.ts.
 *
 * Crossfade lifecycle (CB-140): `play()` resolves as soon as the INCOMING
 * element is actually playing and flips the caller-facing "current" element to
 * it immediately. The gain ramp then runs in the background. Before this the
 * whole 3–8 s fade was awaited inside play(), so for the entire fade the caller
 * held the OUTGOING element as `audioRef` — now-playing reported the new title
 * with the old element's position/duration, and seek/stop operated on a track
 * that was being faded to silence. Flipping up front makes position, duration,
 * seek and the multi-room alignment seek all address the track that is actually
 * becoming current, while the outgoing element (now role "idle") drives nothing.
 */

import type { MediaElementRole } from "./media-event-router.ts";

// The pure crossfade policy (mode/duration/guard/ramps) now lives in
// @workspace/player-core so every surface shares it. Imported locally for the
// mixer's own use, and re-exported so the web player's existing importers
// (use-playback.tsx, tests) are unchanged.
import {
  readCrossfadeMode,
  readCrossfadeDurationSec,
  shouldCrossfadeTransition,
  crossfadeGainSteps,
  type CrossfadeMode,
} from "@workspace/player-core";

export {
  readCrossfadeMode,
  readCrossfadeDurationSec,
  shouldCrossfadeTransition,
  crossfadeGainSteps,
  type CrossfadeMode,
};

/** Injectable construction seams so the mixer is unit-testable without a DOM. */
export interface AudioMixerDeps {
  /** Factory for the second crossfade element. Defaults to `new Audio()`. */
  createSecondary?: () => HTMLAudioElement;
  /**
   * Factory for the Web Audio context. Defaults to `new AudioContext()` when
   * the global exists; crossfade is unavailable otherwise.
   */
  createContext?: () => AudioContext;
}

export class AudioMixer {
  private ctx: AudioContext | null = null;
  private primary: HTMLAudioElement;
  private secondary: HTMLAudioElement;
  private primaryGain: GainNode | null = null;
  private secondaryGain: GainNode | null = null;
  private active: "primary" | "secondary" = "primary";
  private crossfading = false;
  private webAudioReady = false;
  private createContextFn: (() => AudioContext) | null;
  /**
   * A leg whose `play()` REJECTED (autoplay block / dead URL). Kept marked
   * "incoming" until the next play so a late media `error` event from the
   * failed track still routes to the failure policy instead of being dropped as
   * idle noise. On a SUCCESSFUL crossfade the incoming element becomes `active`
   * immediately, so this stays null in that case — the outgoing element that is
   * fading out is deliberately role "idle" (it must drive nothing).
   */
  private incoming: HTMLAudioElement | null = null;
  /**
   * Snaps the in-flight fade to its end state (silence + release the outgoing
   * element) and resolves its background promise. Null when no ramp is running.
   * Two tick loops must never run concurrently over the same gain nodes: the
   * loser would fight the winner over the shared gains — so every new
   * play()/pause() cancels the ramp first.
   */
  private cancelFade: (() => void) | null = null;
  /** Monotonic play() token: lets a superseded call abandon its work. */
  private playGeneration = 0;
  /** Base output level last requested by the player (per-track, 0–1). */
  private baseVolume = 1;
  /**
   * Runtime multiplier applied on top of `baseVolume` (0–1) — used to duck the
   * active channel while a `muteOthers` slot plays. 1 = no ducking.
   */
  private slotGain = 1;

  constructor(primary: HTMLAudioElement, deps: AudioMixerDeps = {}) {
    this.primary = primary;
    this.secondary = deps.createSecondary
      ? deps.createSecondary()
      : new Audio();
    this.secondary.preload = "auto";
    // Cross-origin audio (presigned R2 uploads, the R2 catalogue) MUST have
    // crossOrigin set before a `src` is ever assigned and before
    // createMediaElementSource() runs (see ensureWebAudio() below) — otherwise
    // the browser marks the decoded signal opaque/tainted and Web Audio
    // silently outputs silence once the crossfade graph is wired, even though
    // the element itself appears to play normally. Setting it here, at
    // construction — before either element's `src` is ever touched (that only
    // happens later, inside play()) — guarantees the ordering regardless of
    // which play() call first loads a track. This requires the R2 bucket(s)
    // serving the track URL to answer GET with an Access-Control-Allow-Origin
    // header covering the player's origin (see docs/r2-setup.md).
    this.primary.crossOrigin = "anonymous";
    this.secondary.crossOrigin = "anonymous";
    this.createContextFn =
      deps.createContext ??
      (typeof AudioContext === "undefined" ? null : () => new AudioContext());
  }

  get isCrossfading(): boolean {
    return this.crossfading;
  }

  /** Lazily wire Web Audio — only when crossfade is needed. */
  private ensureWebAudio(): boolean {
    if (this.webAudioReady) return this.canCrossfade();
    if (!this.createContextFn) return false;
    try {
      this.ctx = this.createContextFn();
      this.primaryGain = this.ctx.createGain();
      this.secondaryGain = this.ctx.createGain();
      const srcA = this.ctx.createMediaElementSource(this.primary);
      const srcB = this.ctx.createMediaElementSource(this.secondary);
      srcA.connect(this.primaryGain).connect(this.ctx.destination);
      srcB.connect(this.secondaryGain).connect(this.ctx.destination);
      this.primaryGain.gain.value = this.active === "primary" ? 1 : 0;
      this.secondaryGain.gain.value = this.active === "secondary" ? 1 : 0;
      // Once the GainNodes are the output sink, HTMLMediaElement.volume must
      // stay at 1 on BOTH elements forever: the MediaElementAudioSourceNode
      // still honours element.volume, so any value < 1 there multiplies the
      // gain node and squares the operator's level (CB-141 "volume stuck low").
      // Neutralise whatever the standard path may have written before wiring.
      this.primary.volume = 1;
      this.secondary.volume = 1;
      this.webAudioReady = true;
      return true;
    } catch {
      this.ctx = null;
      this.primaryGain = null;
      this.secondaryGain = null;
      this.webAudioReady = false;
      return false;
    }
  }

  private activeEl(): HTMLAudioElement {
    return this.active === "primary" ? this.primary : this.secondary;
  }

  private idleEl(): HTMLAudioElement {
    return this.active === "primary" ? this.secondary : this.primary;
  }

  private activeGain(): GainNode | null {
    return this.active === "primary" ? this.primaryGain : this.secondaryGain;
  }

  private idleGain(): GainNode | null {
    return this.active === "primary" ? this.secondaryGain : this.primaryGain;
  }

  /** Whether Web Audio routing is active (crossfade path only). */
  canCrossfade(): boolean {
    return Boolean(this.ctx && this.primaryGain && this.secondaryGain);
  }

  /** `null` when using direct HTMLAudioElement output (standard mode). */
  getContextState(): AudioContextState | null {
    return this.ctx?.state ?? null;
  }

  usesWebAudio(): boolean {
    return this.webAudioReady;
  }

  /**
   * Write the current effective level to the single correct sink. When Web
   * Audio is wired the GainNode is the ONLY sink and element.volume stays 1 (see
   * ensureWebAudio); otherwise the element's own volume is the sink. Never both
   * — writing both squares the level.
   */
  private writeActiveLevel(): void {
    const v = this.effectiveVolume();
    if (this.canCrossfade() && this.activeGain()) {
      this.activeGain()!.gain.value = v;
    } else {
      this.activeEl().volume = v;
    }
  }

  private applyVolume(vol: number, element: HTMLAudioElement): void {
    this.baseVolume = Math.max(0, Math.min(1, vol));
    const v = this.effectiveVolume();
    if (this.canCrossfade() && this.activeGain()) {
      this.activeGain()!.gain.value = v;
    } else {
      element.volume = v;
    }
  }

  /** Base level scaled by the runtime slot-gain duck multiplier, clamped 0–1. */
  private effectiveVolume(): number {
    return Math.max(0, Math.min(1, this.baseVolume * this.slotGain));
  }

  /**
   * Set the runtime duck multiplier (0–1) applied on top of the base track
   * volume, and re-apply it to the live sink without restarting playback. Used
   * to silence/restore the music or ad bed while a `muteOthers` announcement is
   * on air. While a fade is running the tick loop reads effectiveVolume() each
   * step, so the new multiplier is honoured on both legs automatically.
   */
  setSlotGain(gain: number): void {
    const next = Math.max(0, Math.min(1, Number.isFinite(gain) ? gain : 1));
    if (next === this.slotGain) return;
    this.slotGain = next;
    if (!this.crossfading) this.writeActiveLevel();
  }

  async resumeContext(): Promise<void> {
    if (this.ctx?.state === "suspended") {
      await this.ctx.resume().catch(() => undefined);
    }
  }

  /**
   * Play `url` on the active element, or crossfade from the current track when
   * `crossfade` is true and a track is already playing. Resolves with the
   * element that now carries `url` — for a crossfade that is the incoming
   * element, returned as soon as it is playing (the fade continues in the
   * background), so the caller's `audioRef`, position/duration reporting and
   * seek all address the new track immediately.
   *
   * `autoPlay: false` LOADS the track without sounding it: the operator has
   * paused, so the engine still follows the schedule (the right thing plays on
   * resume) but the venue stays silent. A silent load never crossfades — there
   * is nothing to fade between.
   */
  async play(
    url: string,
    opts: {
      crossfade: boolean;
      durationSec: number;
      volume: number;
      autoPlay?: boolean;
    },
  ): Promise<HTMLAudioElement> {
    const gen = ++this.playGeneration;
    // Snap any running fade to its end state first (release the outgoing
    // element) so this play() starts from a clean single-active state.
    this.cancelFade?.();
    const vol = opts.volume;
    const autoPlay = opts.autoPlay !== false;
    const wantsCrossfade =
      autoPlay && opts.crossfade && Boolean(this.activeEl().src);

    if (!autoPlay) {
      const el = this.activeEl();
      this.incoming = null;
      el.src = url;
      this.applyVolume(vol, el);
      return el;
    }

    if (wantsCrossfade) {
      this.ensureWebAudio();
      await this.resumeContext();
      if (gen !== this.playGeneration) return this.activeEl();
    }

    if (!wantsCrossfade || !this.canCrossfade()) {
      const el = this.activeEl();
      this.incoming = null;
      el.src = url;
      this.applyVolume(vol, el);
      await el.play();
      return el;
    }

    const incoming = this.idleEl();
    const outGain = this.activeGain();
    const inGain = this.idleGain();
    if (!outGain || !inGain || !this.ctx) {
      const el = this.activeEl();
      this.incoming = null;
      el.src = url;
      this.applyVolume(vol, el);
      await el.play();
      return el;
    }

    this.crossfading = true;
    this.baseVolume = Math.max(0, Math.min(1, vol));
    // Mark the incoming leg so its pre-flip `play`/`loadedmetadata`/`error`
    // route (role "incoming") while it loads and starts.
    this.incoming = incoming;
    incoming.src = url;
    inGain.gain.value = 0;
    try {
      await incoming.play();
    } catch (err) {
      // Keep `incoming` set: the failed element may still emit a media
      // `error` event that must reach the failure policy via event routing.
      if (gen === this.playGeneration) this.crossfading = false;
      throw err;
    }
    if (gen !== this.playGeneration) {
      // Superseded during the load await — the newer play() owns all state.
      return this.activeEl();
    }

    // Flip the caller-facing current element NOW: the incoming track is on air
    // (fading in), so position/duration/seek must address it, not the outgoing
    // element. The outgoing element becomes role "idle" and drives nothing. The
    // incoming marker is cleared because the element is now `active`.
    const outgoing = this.activeEl();
    this.active = this.active === "primary" ? "secondary" : "primary";
    this.incoming = null;

    this.startBackgroundFade(outgoing, outGain, inGain, opts.durationSec, gen);

    return this.activeEl();
  }

  /**
   * Run the gain ramp for a crossfade in the background (play() has already
   * flipped `active` and returned). `outGain` fades to 0, `inGain` fades to the
   * current effective level; at the end the outgoing element is paused and its
   * src released. Cancellable (a new play()/pause()/dispose snaps it to the end
   * state) so two ramps never fight over the same gains.
   */
  private startBackgroundFade(
    outgoing: HTMLAudioElement,
    outGain: GainNode,
    inGain: GainNode,
    durationSec: number,
    gen: number,
  ): void {
    const { from, to } = crossfadeGainSteps(durationSec);
    const stepMs = (durationSec * 1000) / (from.length - 1);
    let i = 0;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      this.cancelFade = null;
      outGain.gain.value = 0;
      inGain.gain.value = this.effectiveVolume();
      this.crossfading = false;
      // Only tear down the outgoing element if a newer play() has not already
      // repurposed it (gen guard): a superseded fade must not pause the track
      // the newer call started.
      if (gen === this.playGeneration || outgoing !== this.activeEl()) {
        outgoing.pause();
        outgoing.removeAttribute("src");
      }
    };
    this.cancelFade = finish;
    const tick = () => {
      if (finished) return;
      // Read the effective level each tick so a duck/restore that lands
      // mid-crossfade is honoured on both legs.
      const target = this.effectiveVolume();
      outGain.gain.value = from[i]! * target;
      inGain.gain.value = to[i]! * target;
      i += 1;
      if (i >= from.length) {
        finish();
        return;
      }
      setTimeout(tick, stepMs);
    };
    tick();
  }

  setVolume(volume: number): void {
    this.baseVolume = Math.max(0, Math.min(1, volume));
    if (this.crossfading) {
      // The running tick loop reads effectiveVolume() each step and will apply
      // the new base to both legs; writing here would fight it.
      return;
    }
    this.writeActiveLevel();
  }

  pause(): void {
    // Snap an in-flight fade to its end state first so a later resume plays
    // the element that actually carries the newest track.
    this.cancelFade?.();
    this.primary.pause();
    this.secondary.pause();
  }

  getCurrentElement(): HTMLAudioElement {
    return this.activeEl();
  }

  /** Both elements, for attaching the shared listener set once each. */
  getElements(): readonly [HTMLAudioElement, HTMLAudioElement] {
    return [this.primary, this.secondary];
  }

  /**
   * Role of `el` for event routing: "active" (on air — during a crossfade this
   * is the INCOMING track, flipped up front), "incoming" (a leg whose play()
   * rejected, retained until the next play so its error routes), or "idle" (the
   * outgoing/faded-out/unused element, whose events are teardown noise).
   */
  elementRole(el: HTMLMediaElement): MediaElementRole {
    if (el === this.activeEl()) return "active";
    if (this.incoming !== null && el === this.incoming) return "incoming";
    return "idle";
  }

  /**
   * Stop both elements and release the Web Audio graph. For hook teardown —
   * the mixer is unusable afterwards (a new one is built per effect run).
   */
  dispose(): void {
    this.cancelFade?.();
    this.primary.pause();
    this.secondary.pause();
    this.primary.removeAttribute("src");
    this.secondary.removeAttribute("src");
    this.incoming = null;
    this.crossfading = false;
    if (this.ctx) void this.ctx.close().catch(() => undefined);
    this.ctx = null;
    this.primaryGain = null;
    this.secondaryGain = null;
    this.webAudioReady = false;
  }
}
